// Client-side head manager. RouteTracker (App.js) calls applyRouteSeo on every
// navigation; pages with data-driven metadata (blog posts, public listings) call
// it again with overrides once their data arrives, and use the exported helpers
// for JSON-LD and extra tags.
//
// Everything here runs only in browsers that execute JavaScript. Crawlers that do
// not (OpenAI, Anthropic, Perplexity, social scrapers) see public/index.html only.

const DEFAULT_TITLE = 'KeyTa';
const DEFAULT_DESCRIPTION =
  'KeyTa helps buyers, sellers, and agents with AI-powered real estate insights, listings, and workflows.';
const DEFAULT_OG_IMAGE = '/og-default.png';
const SITE_NAME = 'KeyTa';

const INDEXABLE_ROBOTS = 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1';
// noarchive keeps private pages out of Bing Copilot answers as well as the index.
const NOINDEX_ROBOTS = 'noindex,nofollow,noarchive';

const INDEXABLE_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/about\/?$/,
  /^\/market\/?$/,
  /^\/faq\/?$/,
  /^\/contact\/?$/,
  /^\/privacy-policy\/?$/,
  /^\/terms-of-service\/?$/,
  /^\/properties\/?$/,
  /^\/properties-map\/?$/,
  /^\/property\/[^/]+\/?$/, // active MLS listings; PropertyDetail sets per-listing tags
  /^\/blog\/?$/,
  /^\/blog\/[^/]+\/?$/,
  /^\/[^/]+\/[^/]+\/[^/]+\/home\/[^/]+\/?$/,
];

const TITLE_BY_PATH = {
  '/': 'KeyTa | AI Real Estate Assistant',
  '/about': 'About KeyTa | AI Real Estate Platform',
  '/market': 'Market Insights | KeyTa',
  '/faq': 'FAQ | KeyTa',
  '/contact': 'Contact | KeyTa',
  '/privacy-policy': 'Privacy Policy | KeyTa',
  '/terms-of-service': 'Terms of Service | KeyTa',
  '/properties': 'Find Properties | KeyTa',
  '/properties-map': 'Property Map Search | KeyTa',
  '/blog': 'Blog | Real Estate Insights | KeyTa',
};

const DESCRIPTION_BY_PATH = {
  '/': 'Explore AI-powered support for buying, selling, and managing properties with KeyTa.',
  '/about': 'Learn about KeyTa and our mission to modernize real estate with practical AI tools.',
  '/market': 'Read local market updates, trends, and pricing insights for smarter real estate decisions.',
  '/faq': 'Find answers to common questions about KeyTa, listings, and real estate workflows.',
  '/contact': 'Contact the KeyTa team for support with listings, planning, and real estate operations.',
  '/properties': 'Browse available properties and discover listing details with KeyTa.',
  '/properties-map': 'Search properties on an interactive map and view listing details in KeyTa.',
  '/blog': 'Guides on pricing, listing, offers, closing, and costs when selling a home in Ontario.',
};

export function getConfiguredSiteOrigin() {
  const envOrigin =
    process.env.REACT_APP_PUBLIC_SITE_URL ||
    process.env.REACT_APP_WEB_URL ||
    process.env.REACT_APP_FRONTEND_URL ||
    '';

  const normalizedEnvOrigin = envOrigin.trim().replace(/\/+$/, '');
  if (normalizedEnvOrigin) {
    return normalizedEnvOrigin;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return '';
}

function normalizePath(pathname = '') {
  if (!pathname || pathname === '/') return '/';
  const cleaned = pathname.replace(/\/+$/, '');
  return cleaned || '/';
}

export function isIndexablePath(pathname = '') {
  const normalizedPath = normalizePath(pathname);
  return INDEXABLE_ROUTE_PATTERNS.some((pattern) => pattern.test(normalizedPath));
}

export function toAbsoluteUrl(value) {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  const origin = getConfiguredSiteOrigin();
  return `${origin}${value.startsWith('/') ? '' : '/'}${value}`;
}

export function upsertMeta(key, content, attr = 'name') {
  if (typeof document === 'undefined' || !key || !content) return;

  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

export function removeMeta(key, attr = 'name') {
  if (typeof document === 'undefined' || !key) return;
  const tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (tag) tag.remove();
}

export function upsertCanonical(href) {
  if (typeof document === 'undefined' || !href) return;

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', href);
}

// One <script type="application/ld+json"> per id; pages remove theirs on unmount so
// structured data never leaks onto the next route.
export function upsertJsonLd(id, data) {
  if (typeof document === 'undefined' || !id || !data) return;

  let script = document.head.querySelector(`script[data-seo="${id}"]`);
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-seo', id);
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

export function removeJsonLd(id) {
  if (typeof document === 'undefined' || !id) return;
  const script = document.head.querySelector(`script[data-seo="${id}"]`);
  if (script) script.remove();
}

/**
 * Write title, description, robots, canonical, Open Graph and Twitter tags for a path.
 *
 * @param {string} pathname  route path (location.pathname)
 * @param {object} overrides optional { title, description, image, type, canonical, robots }
 */
export function applyRouteSeo(pathname = '', overrides = {}) {
  if (typeof document === 'undefined') return;

  const normalizedPath = normalizePath(pathname);
  const origin = getConfiguredSiteOrigin();
  const canonicalUrl = overrides.canonical || `${origin}${normalizedPath}`;
  const indexable = isIndexablePath(normalizedPath);

  const title = overrides.title || TITLE_BY_PATH[normalizedPath] || DEFAULT_TITLE;
  const description = overrides.description || DESCRIPTION_BY_PATH[normalizedPath] || DEFAULT_DESCRIPTION;
  const robots = overrides.robots || (indexable ? INDEXABLE_ROBOTS : NOINDEX_ROBOTS);
  const image = toAbsoluteUrl(overrides.image || DEFAULT_OG_IMAGE);
  const ogType = overrides.type || 'website';

  document.title = title;
  upsertCanonical(canonicalUrl);

  upsertMeta('description', description, 'name');
  upsertMeta('robots', robots, 'name');

  upsertMeta('og:site_name', SITE_NAME, 'property');
  upsertMeta('og:locale', 'en_CA', 'property');
  upsertMeta('og:type', ogType, 'property');
  upsertMeta('og:title', title, 'property');
  upsertMeta('og:description', description, 'property');
  upsertMeta('og:url', canonicalUrl, 'property');
  upsertMeta('og:image', image, 'property');

  upsertMeta('twitter:card', 'summary_large_image', 'name');
  upsertMeta('twitter:title', title, 'name');
  upsertMeta('twitter:description', description, 'name');
  upsertMeta('twitter:image', image, 'name');
}
